import { memo, useEffect, useRef, useState } from 'react';
// State
import useThemeStore from 'Theme/store';
import useTextAreaFocus from './state';
// Styles
import { cn } from 'utils/styles';
// Components
import { Box, IconButton } from '@mui/material';
import Editor from 'react-simple-code-editor';
// Icons
import { Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import FileUpload, { onChangeFile } from '../../../Components/Forms/FileUpload';
import useSmartModeStore from '../../../Features/SmartMode/store';
import CapabilitiesIcon from '../../../Theme/icons/capabilitiesIcon';
import PaperClipIcon from '../../../Theme/icons/paperclip';
import useDragAndDrop from './useDragAndDrop';

const TextAreaHightlight = ({
  value,
  onChange,
  triggerAnimation,
  highlightWords,
  autoFocus,
  disabled,
  onKeyDown,
  maxRows,
  minRows,
  placeholder = `Chat here or type '/' for prompt templates`,
  classNameContainer,
  classNameMainContainer,
  children,
  warningAction,
  onInteraction,
  models,
  setShowFileDialog,
  setShowCapabilitiesDialog,
  startRecording,
  stopRecording,
  onSend,
  inputWarning = false,
  rtlDir = true,
  noActions = false,
  color,
  checkSmartMode = true,
  onCopy = null,
  pricingMethod = 'perWord',
  showButtons = ['microphone', 'file', 'capabilities', 'send'],
}) => {
  const MAX_WORDS_PER_MESSAGE = 2000;
  const [maxHeight, setMaxHeight] = useState(false);
  const [wordLimitExceeded, setWordLimitExceeded] = useState(false);

  const mainContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [fileSelected, setFileSelected] = useState([]);

  const { smartMode } = useSmartModeStore();
  const { theme: themeValue } = useThemeStore();

  function escapeHTML(str) {
    const map = {
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;',
      '"': '&quot;',
      "'": '&apos;',
    };

    // eslint-disable-next-line no-useless-escape
    return str.replace(/[\<\>\&\"\']/g, (match) => map[match]);
  }

  // Function to count words in the text
  const countWords = (text) => {
    if (!text || typeof text !== 'string') return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  // !!IMPORTANT Be careful with this function, it affect the styles of prompt templates
  const highlight = (code) => {
    // First add zero-width space after < when it's not followed by a space
    let highlightedCode = code.replace(/<(?!\s)/g, '<\u200B');

    // Then apply the HTML escaping function
    highlightedCode = highlightedCode.replace(/<[^>]+>/g, (match) =>
      escapeHTML(match)
    );

    highlightWords.forEach((highlightConfig) => {
      const { highlight, className, prefix, suffix } = highlightConfig;
      if (highlight && className) {
        highlightedCode = highlightedCode.replace(
          highlight,
          `<span class="${className}">${prefix ?? ''}$1${suffix ?? ''}</span>`
        );
      }
    });

    return highlightedCode;
  };

  // Focus management
  const { focusRequest, blur } = useTextAreaFocus();
  function focusEditor() {
    containerRef.current._input.focus();
    blur();
  }

  useEffect(() => {
    if (autoFocus || focusRequest) focusEditor();
  }, [autoFocus, focusRequest]);

  // Adjust container height
  useEffect(() => {
    adjustContainerHeight();
  }, [value, highlightWords, minRows, maxRows]);

  const handleChange = (newValue) => {
    // Check if we need to enforce the word limit for perMessage pricing
    if (pricingMethod === 'perMessage') {
      const wordCount = countWords(newValue);
      if (wordCount > MAX_WORDS_PER_MESSAGE) {
        // Only show the error toast when first exceeding the limit
        if (!wordLimitExceeded) {
          toast.error(
            `Maximum ${MAX_WORDS_PER_MESSAGE} words allowed per message with this pricing plan. Please reduce your message length.`,
            {
              duration: 4000,
              id: 'word-limit-exceeded',
            }
          );
        }
        setWordLimitExceeded(true);
      } else {
        setWordLimitExceeded(false);
      }
    }

    // Always allow the change to go through
    onChange(newValue);
    adjustContainerHeight();
  };

  const adjustContainerHeight = () => {
    const textareaRef = containerRef.current._input;
    const textareaContainerRef = mainContainerRef.current;
    if (textareaRef) {
      // Calculate dynamic maxHeight based on viewport height
      const viewportHeight = window.innerHeight;
      const dynamicMaxHeight = Math.min(
        Math.max(viewportHeight * 0.4, 150), // 40% of viewport height, minimum 150px
        500
      );

      textareaContainerRef.style.maxHeight = `${dynamicMaxHeight}px`;
      textareaContainerRef.style.overflowY = 'scroll';
    }
  };

  // Set spellcheck and other attributes on each input instance.
  // !!!Spellcheck is off if highlighted text is present!!!
  useEffect(() => {
    const inputElements = mainContainerRef?.current?.querySelectorAll(
      '.npm__react-simple-code-editor__textarea'
    );

    if (inputElements) {
      for (const inputElement of inputElements) {
        // Check if the current value contains any highlighted patterns
        const hasHighlightedText = highlightWords.some(({ highlight }) =>
          highlight.test(value)
        );

        // Disable autocorrect features if highlighted text is present
        inputElement.autocomplete = hasHighlightedText ? 'off' : 'on';
        inputElement.autocorrect = hasHighlightedText ? 'off' : 'on';
        inputElement.spellcheck = !hasHighlightedText;

        inputElement.setAttribute(
          'autocapitalize',
          hasHighlightedText ? 'off' : 'on'
        );
        inputElement.setAttribute(
          'data-gramm',
          hasHighlightedText ? 'false' : 'true'
        );
      }
    }
  }, [value, highlightWords]);

  const navigate = useNavigate();

  const handleNavigation = (e) => {
    e.preventDefault();
    const path = e.currentTarget.getAttribute('href');
    navigate(path);

    setTimeout(() => {
      const targetSection = document.querySelector('#wordcapLimitsSection');
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth' });
        targetSection.focus();
      }
    }, 300);
  };

  const noVision = () => {
    return models?.every((model) => model.type != 'vision');
  };

  // Utilizar el hook personalizado para drag and drop
  const { dragActive, handleDrag, handleDrop, handlePaste } = useDragAndDrop({
    onChangeFile,
    onInteraction,
    setFileSelected,
    noVision,
  });

  const handleKeyDown = (e) => {
    // Prevent sending when over word limit if it's an Enter keypress
    if (pricingMethod === 'perMessage' && wordLimitExceeded) {
      // Check if it's an Enter key without shift/alt/ctrl/meta
      if (
        e.key === 'Enter' &&
        !e.shiftKey &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        // Show a toast reminder
        toast.error(
          `Please reduce your message to ${MAX_WORDS_PER_MESSAGE} words or less before sending.`,
          {
            duration: 3000,
            id: 'enter-key-blocked',
          }
        );

        return;
      }
    }

    // Otherwise, pass the event to the parent handler
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <Box
      dir={rtlDir && 'rtl'}
      ref={mainContainerRef}
      className={cn(
        `relative rounded-[14px] !h-[inherit] notranslate p-[10px] overflow-rtl ${
          themeValue == 'dark'
            ? 'overflow-rtl-dark bg-night-black text-crystal-bell border-stargate-shimmer'
            : 'overflow-rtl-light bg-white text-raisin-black border-nue-blue'
        } border mt-2 transition-all duration-300 ease-in-out font-figtree`,
        {
          'md:border-purple-dark': themeValue === 'dark' && smartMode !== 'on',
          'md:border-purple': themeValue !== 'dark' && smartMode !== 'on',
          'border-neon-custom': themeValue !== 'dark' && smartMode === 'on',
          'border-neon-dark-custom':
            themeValue === 'dark' && smartMode === 'on',
          'overflow-visible': true,
          'transform scale-105 opacity-75': dragActive,
          'neon-effect-custom': smartMode === 'on',
          'border-red-500 border-2':
            pricingMethod === 'perMessage' && wordLimitExceeded,
        },
        classNameMainContainer
      )}
      onDrop={handleDrop}
      onDragOver={handleDrag}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
    >
      <div dir="ltr">
        {dragActive ? (
          <div className="flex items-center justify-center h-full p-4">
            <span
              className={`${
                themeValue === 'dark' ? 'text-palladium' : 'text-cool-gray'
              } text-m font-medium uppercase opacity-75`}
            >
              {noVision()
                ? 'Drag and drop your files here (images not supported)'
                : 'Drag and drop your files here to insert it'}
            </span>
          </div>
        ) : (
          <>
            <div style={{ display: 'none' }}>
              <FileUpload
                type=".png, .jpeg, .jpg, .webp, .gif, .mp3, .mp4, .pdf, .docx, .txt, .pptx, .xlsx, .html, .csv, .json, .py, .php, .js, .css, .cs, .swift, .kt, .xml, .ts, .md"
                isHidden={true}
                onUploaded={(uploadedFiles) => {
                  setFileSelected(uploadedFiles);
                  onInteraction(uploadedFiles);
                }}
              />
            </div>
            {children}
            <div
              className={`w-full flex relative ${
                themeValue === 'dark'
                  ? 'text-sonic-silver'
                  : 'text-battleship-gray'
              } ${onCopy && 'relative items-start gap-2'}`}
            >
              {onCopy && (
                <IconButton onClick={onCopy} className="!sticky top-0 left-0">
                  <ClipboardDocumentIcon className="w-5 h-5" />
                </IconButton>
              )}
              <Editor
                ref={containerRef}
                value={value}
                onValueChange={handleChange}
                padding={10}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                autoFocus={autoFocus}
                disabled={disabled}
                highlight={highlight}
                className={cn(
                  `flex flex-1 font-figtree notranslate text-sm shadow-sm px-4 py-3 rounded font-regular ${
                    themeValue === 'dark'
                      ? 'bg-night-black text-crystal-bell dark:bg-night-black dark:text-crystal-bell'
                      : 'bg-white text-raisin-black'
                  }`,
                  {
                    'move-up-and-fade': triggerAnimation && smartMode === 'on',
                  },
                  classNameContainer
                )}
                textareaClassName="bg-transparent w-full h-full !flex !p-0"
                preClassName="!block !p-0"
                autoCorrect="on"
                autoComplete="on"
                spellCheck="true"
              />
              {!noActions && (
                <div class="sticky top-5 flex justify-center overflow-visible ">
                  <>
                    {showButtons.includes('microphone') &&
                      (isRecording ? (
                        <Tooltip title="Finish recording" arrow placement="top">
                          <StopIcon
                            className="max-md:hidden sticky top-0 w-5 h-5 cursor-pointer select-none mr-2 text-red-500 !ml-[-2rem]"
                            onClick={() => {
                              stopRecording();
                              setIsRecording(false);
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Record a prompt" arrow placement="top">
                          <MicrophoneIcon
                            className="max-md:hidden sticky top-0 w-5 h-5 cursor-pointer select-none mr-2 !ml-[-2rem]"
                            onClick={() => {
                              startRecording();
                              setIsRecording(true);
                            }}
                          />
                        </Tooltip>
                      ))}
                    {showButtons.includes('file') && (
                      <Tooltip title="Attach a file" arrow placement="top">
                        <span className="mr-2 -mt-px">
                          <PaperClipIcon
                            className="hidden sticky top-0 md:block w-[22px] h-[22px] cursor-pointer select-none scale-x-[-1]"
                            onClick={() => setShowFileDialog(true)}
                          />
                        </span>
                      </Tooltip>
                    )}
                    {showButtons.includes('capabilities') && (
                      <Tooltip
                        title="Select capabilities"
                        arrow
                        placement="top"
                      >
                        <button
                          onClick={() => {
                            setShowCapabilitiesDialog(true);
                          }}
                          className="hidden sticky top-0 md:flex items-center justify-center focus:outline-none h-5 -mt-px"
                        >
                          <CapabilitiesIcon className="w-5 h-5 cursor-pointer select-none" />
                        </button>
                      </Tooltip>
                    )}
                    {showButtons.includes('send') &&
                      value?.length > 0 &&
                      !wordLimitExceeded && (
                        <div
                          className="sticky top-0 w-6.5 h-5 cursor-pointer select-none ml-2"
                          onClick={onSend}
                        >
                          <div className="bg-violet-blue text-soft-white rounded-lg p-1 pl-[6px] pr-[2px] pb-[6px] pt-[2px] -mt-[2px]">
                            <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
                          </div>
                        </div>
                      )}
                    {value?.length > 0 && wordLimitExceeded && (
                      <div className="sticky top-0 w-6.5 h-5 select-none ml-2 opacity-50">
                        <div className="bg-gray-400 text-soft-white rounded-lg p-1 pl-[6px] pr-[2px] pb-[6px] pt-[2px] -mt-[2px]">
                          <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
                        </div>
                      </div>
                    )}
                  </>
                </div>
              )}
            </div>
            {inputWarning && (
              <div className="sticky mt-2 bottom-2 left-1 flex px-2 py-1 bg-nue-blue text-white text-xs font-medium gap-2 rounded w-fit">
                <ExclamationTriangleIcon className="w-4 h-4" />
                <div className="select-none">
                  Attention: You've reached the word limit for this request. You
                  can start a{' '}
                  <span
                    className="underline cursor-pointer"
                    onClick={warningAction}
                  >
                    new chat
                  </span>{' '}
                  or{' '}
                  <a
                    className="underline"
                    href="/user-settings"
                    onClick={handleNavigation}
                  >
                    adjust wordcap limits
                  </a>{' '}
                  in your settings.
                </div>
              </div>
            )}
            {pricingMethod === 'perMessage' &&
              !wordLimitExceeded &&
              value &&
              countWords(value) > MAX_WORDS_PER_MESSAGE * 0.8 && (
                <div className="sticky mt-2 bottom-2 left-1 flex px-2 py-1 bg-gray-100 dark:bg-gray-800 text-xs font-medium gap-2 rounded w-fit">
                  <div className="select-none">
                    <span
                      className={`${
                        countWords(value) > MAX_WORDS_PER_MESSAGE * 0.9
                          ? 'text-red-500 font-bold'
                          : 'text-orange-500 font-bold'
                      }`}
                    >
                      {countWords(value)}
                    </span>
                    <span className="text-gray-500">
                      /{MAX_WORDS_PER_MESSAGE} words
                    </span>
                  </div>
                </div>
              )}
            {pricingMethod === 'perMessage' && wordLimitExceeded && (
              <div className="sticky mt-2 bottom-2 left-1 flex px-2 py-1 bg-red-500 text-white text-xs font-medium gap-2 rounded w-fit animate-pulse">
                <ExclamationTriangleIcon className="w-4 h-4" />
                <div className="select-none flex items-center">
                  <span className="font-bold mr-1">
                    {countWords(value)}/{MAX_WORDS_PER_MESSAGE}
                  </span>
                  words - Please delete some text to continue.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.models === nextProps.models &&
    prevProps.children === nextProps.children &&
    prevProps.autoFocus === nextProps.autoFocus &&
    prevProps.onKeyDown === nextProps.onKeyDown &&
    prevProps.startRecording === nextProps.startRecording &&
    prevProps.stopRecording === nextProps.stopRecording &&
    prevProps.disabled === nextProps.disabled
  );
};

export default memo(TextAreaHightlight, areEqual);
